import {
  differenceInDays,
  formatDistanceToNow,
  isValid,
  parseISO
} from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

const US_FORMAT_STRINGS = {
  "month-day-year": "MM/dd/yyyy", // "09/12/2022"
  "monthName-day-year": "MMMM d',' yyyy", // "September 12, 2022"
  "month-year": "MM/yyyy", // "09/2022"
  "shortMonthName-shortDay-year": "MMM d',' yyyy", // "Sep 12, 2022"
  "shortMonth-day-year": "M/d/yyyy", // "9/12/2022"
  "year-month-day": "yyyy-MM-dd", // "2022-09-12"
  "monthName-day-year-time-tz": "MMMM dd yyyy' at 'hh':'mmaaa zzz" // "September 12 2022 at 12:00am EDT"
};

const DAY_FIRST_FORMAT_STRINGS = {
  "month-day-year": "dd/MM/yyyy", // "12/09/2022"
  "monthName-day-year": "do MMMM yyyy", // "12th September 2022"
  "month-year": "MM/yyyy", // "09/2022"
  "shortMonthName-shortDay-year": "d MMM yyyy", // "12 Sep 2022", "5 Sep 2022"
  "shortMonth-day-year": "d/M/yyyy", // "12/9/2022"
  "year-month-day": "yyyy-MM-dd", // "2022-09-12"
  "monthName-day-year-time-tz": "dd MMMM yyyy' at 'hh':'mmaaa zzz" // "12 September 2022 at 12:00am EDT"
};

const DEFAULT_REGION_CODE = "US";

const FORMAT_STRINGS_BY_REGION = {
  US: US_FORMAT_STRINGS,
  GB: DAY_FIRST_FORMAT_STRINGS,
  AU: DAY_FIRST_FORMAT_STRINGS
};

export const getRegionCode = () => {
  const regionCode = window.REGION_CODE;

  return typeof FORMAT_STRINGS_BY_REGION[regionCode] === "object"
    ? regionCode
    : DEFAULT_REGION_CODE;
};

export const getFormatStrings = () => FORMAT_STRINGS_BY_REGION[getRegionCode()];

export const getFormatString = formatString => getFormatStrings()[formatString];

export const dateParser = date => {
  /* 
    The idea is receive date in any format and return a Date object
  */

  if (!date) {
    /* 
      new Date(null) returns dec 31, 1969
      So this check avoid passing through false positive date validations, returning
      a real invalid javascript date object 
    */
    return new Date("");
  }

  if (date instanceof Date) {
    return date;
  }

  return parseISO(date);
};

export const validateAndFormatDate = ({
  date,
  formatString,
  fallbackValue = "",
  nullableValue = ""
}) => {
  if (!date) {
    return nullableValue;
  }

  const currentTimezone =
    window.CURRENT_TIMEZONE || "America/Indiana/Indianapolis";
  const dateObjectBrowserTz = dateParser(date);

  if (!isValid(dateObjectBrowserTz)) {
    return fallbackValue;
  }

  const formatPattern = getFormatString(formatString);

  if (typeof formatPattern !== "string") {
    throw new Error("Invalid date format string");
  }

  const dateTzParsed = zonedTimeToUtc(dateObjectBrowserTz, currentTimezone);

  return formatInTimeZone(dateTzParsed, currentTimezone, formatPattern);
};

export const validateAndFormatDistanceToNow = (date, fallbackValue = "") => {
  const dateObject = dateParser(date);

  return isValid(dateObject) ? formatDistanceToNow(dateObject) : fallbackValue;
};

export const getDifferenceInDays = (firstDate, secondDate) => {
  const parsedFirstDate = dateParser(firstDate);
  const parsedSecondDate = dateParser(secondDate);

  if (isValid(parsedFirstDate) && isValid(parsedSecondDate)) {
    return differenceInDays(parsedFirstDate, parsedSecondDate);
  }

  return "";
};
